import React, { useEffect, useMemo, useState } from "react";
import {
  TestTube, ListOrdered, ClipboardCheck, AlertTriangle, ShieldAlert,
  Package, GraduationCap, Search, Plus, Minus, Loader2, AlertCircle,
  Thermometer, Truck, FileBadge, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  TUBES, ORDER_OF_DRAW, SPECIMEN_TESTS, HANDLING_STEPS,
  LABELING_STANDARDS, PACKAGING_STANDARDS, TEMPERATURE_REQUIREMENTS, TRANSPORT_SOP,
  SHARPS_PROCEDURES, SHARPS_CHECKLIST, EXPOSURE_NEEDLESTICK, EXPOSURE_BLOOD,
  SUPPLY_CATEGORY_META, TRAINING_MODULES, ANNUAL_COMPETENCY,
  STAFF_CREDENTIAL_REQUIREMENTS,
} from "@/components/help/specimenContent";
import { listSupplies, adjustSupply, getSuppliesStats } from "@/lib/api";

// =====================================================================
// Sub-section nav
// =====================================================================
const SUBSECTIONS = [
  { id: "tubes", label: "Tube Reference", icon: TestTube },
  { id: "order", label: "Order of Draw", icon: ListOrdered },
  { id: "guide", label: "Specimen Guide", icon: ClipboardCheck },
  { id: "handling", label: "Handling & Transport", icon: Truck },
  { id: "sharps", label: "Sharps Safety", icon: ShieldAlert },
  { id: "exposure", label: "Exposure Procedures", icon: AlertTriangle },
  { id: "supplies", label: "Supply Checklist", icon: Package },
  { id: "training", label: "Training & Competency", icon: GraduationCap },
];

// =====================================================================
// Shared atoms
// =====================================================================
const Pill = ({ children, color = "#475569", bg = "#F1F5F9" }) => (
  <span
    className="inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
    style={{ background: bg, color }}
  >
    {children}
  </span>
);

const SubIntro = ({ title, subtitle, icon: Icon }) => (
  <div className="flex items-center gap-2.5">
    <span
      className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
      style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}
    >
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <h2 className="font-display text-xl font-semibold" style={{ color: "#0B2545" }}>
        {title}
      </h2>
      <p className="text-xs mt-0.5 max-w-2xl" style={{ color: "#64748B" }}>
        {subtitle}
      </p>
    </div>
  </div>
);

// =====================================================================
// 1. Tube Reference Chart
// =====================================================================
const TubesSection = () => (
  <div className="space-y-3" data-testid="specimen-tubes">
    <SubIntro
      title="Tube Reference Chart"
      subtitle="Color, additive, common uses, processing, and handling for every tube type."
      icon={TestTube}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {TUBES.map((t) => (
        <div
          key={t.id}
          className="rounded-xl border bg-white p-4"
          style={{ borderColor: "#E2E8F0", borderLeftWidth: 4, borderLeftColor: t.hex }}
          data-testid={`tube-${t.id}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <span
              className="h-6 w-6 rounded-full shrink-0 border"
              style={{ background: t.hex, borderColor: "rgba(0,0,0,0.08)" }}
            />
            <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
              {t.color}
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "#64748B" }}>
            Additive
          </div>
          <p className="text-xs mb-2" style={{ color: "#0B2545" }}>{t.additive}</p>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "#64748B" }}>
            Uses
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {t.uses.map((u, i) => (
              <Pill key={i} bg={t.bg} color={t.hex}>{u}</Pill>
            ))}
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "#64748B" }}>
            Processing
          </div>
          <p className="text-xs mb-2" style={{ color: "#475569" }}>{t.processing}</p>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "#64748B" }}>
            Handling
          </div>
          <p className="text-xs" style={{ color: "#475569" }}>{t.handling}</p>
        </div>
      ))}
    </div>
  </div>
);

// =====================================================================
// 2. Order of Draw
// =====================================================================
const OrderOfDrawSection = () => (
  <div className="space-y-3" data-testid="specimen-order">
    <SubIntro
      title="CLSI Order of Draw"
      subtitle="Draw multiple tubes in this exact order to avoid additive carryover."
      icon={ListOrdered}
    />
    <ol className="space-y-2.5">
      {ORDER_OF_DRAW.map((s) => (
        <li
          key={s.step}
          className="rounded-xl border bg-white p-3 flex items-center gap-3"
          style={{ borderColor: "#E2E8F0", borderLeftWidth: 4, borderLeftColor: s.color }}
          data-testid={`order-${s.step}`}
        >
          <span
            className="grid place-items-center h-9 w-9 rounded-full font-display text-sm font-bold shrink-0"
            style={{ background: s.hex, color: "#FFFFFF" }}
          >
            {s.step}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
              {s.name}
            </div>
            <p className="text-xs mt-0.5" style={{ color: "#475569" }}>{s.note}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

// =====================================================================
// 3. Specimen Collection Guide (searchable test catalog)
// =====================================================================
const SpecimenGuideSection = () => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return SPECIMEN_TESTS;
    return SPECIMEN_TESTS.filter(
      (t) =>
        t.test.toLowerCase().includes(s) ||
        t.tube.toLowerCase().includes(s) ||
        (t.notes || "").toLowerCase().includes(s),
    );
  }, [q]);

  return (
    <div className="space-y-3" data-testid="specimen-guide">
      <SubIntro
        title="Specimen Collection Guide"
        subtitle="Tube, volume, fasting, processing, storage, and rejection notes per common test."
        icon={ClipboardCheck}
      />
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
        <Input
          placeholder="Search a test (CBC, lipid, A1C…)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-10"
          data-testid="guide-search"
        />
      </div>
      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-xs">
          <thead style={{ background: "rgba(11,37,69,0.04)" }}>
            <tr style={{ color: "#0B2545" }}>
              <th className="text-left font-semibold px-3 py-2">Test</th>
              <th className="text-left font-semibold px-3 py-2">Tube</th>
              <th className="text-left font-semibold px-3 py-2">Volume</th>
              <th className="text-left font-semibold px-3 py-2">Fasting</th>
              <th className="text-left font-semibold px-3 py-2">Processing</th>
              <th className="text-left font-semibold px-3 py-2">Storage</th>
              <th className="text-left font-semibold px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-6" style={{ color: "#64748B" }}>
                  No matches for &quot;{q}&quot;.
                </td>
              </tr>
            ) : (
              filtered.map((t, i) => (
                <tr
                  key={t.test}
                  style={{ background: i % 2 ? "#FFFFFF" : "rgba(11,37,69,0.02)", color: "#0B2545" }}
                  data-testid={`guide-row-${i}`}
                >
                  <td className="px-3 py-2 font-medium align-top">{t.test}</td>
                  <td className="px-3 py-2 align-top">{t.tube}</td>
                  <td className="px-3 py-2 align-top">{t.volume}</td>
                  <td className="px-3 py-2 align-top">{t.fasting}</td>
                  <td className="px-3 py-2 align-top" style={{ color: "#475569" }}>{t.processing}</td>
                  <td className="px-3 py-2 align-top" style={{ color: "#475569" }}>{t.storage}</td>
                  <td className="px-3 py-2 align-top" style={{ color: "#475569" }}>{t.notes}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =====================================================================
// 4. Handling & Transport
// =====================================================================
const HandlingSection = () => (
  <div className="space-y-4" data-testid="specimen-handling">
    <SubIntro
      title="Handling & Transport SOPs"
      subtitle="Eight-step pickup-to-delivery protocol, labeling/packaging standards, and temperature requirements."
      icon={Truck}
    />

    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "#64748B" }}>
        8-Step Pickup → Delivery
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {HANDLING_STEPS.map((s) => (
          <li
            key={s.n}
            className="rounded-xl border bg-white p-3 flex items-start gap-3"
            style={{ borderColor: "#E2E8F0" }}
            data-testid={`handling-step-${s.n}`}
          >
            <span
              className="grid place-items-center h-7 w-7 rounded-full text-xs font-bold shrink-0"
              style={{ background: "#0B2545", color: "#FFD774" }}
            >
              {s.n}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
                {s.title}
              </div>
              <p className="text-xs mt-0.5" style={{ color: "#475569" }}>{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <ChecklistCard title="Labeling Standards" items={LABELING_STANDARDS} testid="labeling-card" />
      <ChecklistCard title="Packaging Standards" items={PACKAGING_STANDARDS} testid="packaging-card" />
    </div>

    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5" style={{ color: "#64748B" }}>
        <Thermometer className="h-3.5 w-3.5" /> Temperature Requirements
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {TEMPERATURE_REQUIREMENTS.map((t) => (
          <div key={t.kind} className="rounded-xl border bg-white p-3" style={{ borderColor: "#E2E8F0" }}>
            <div className="font-display text-xs font-semibold mb-1.5" style={{ color: "#0B2545" }}>
              {t.kind}
            </div>
            <ul className="space-y-1">
              {t.examples.map((e, i) => (
                <li key={i} className="text-xs" style={{ color: "#475569" }}>• {e}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>

    <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }} data-testid="transport-sop">
      <div className="font-display text-sm font-semibold mb-2" style={{ color: "#0B2545" }}>
        Transport Documentation SOP
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(TRANSPORT_SOP).map(([k, items]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "#64748B" }}>
              {k.replace(/_/g, " ")}
            </div>
            <ul className="space-y-0.5">
              {items.map((it, i) => (
                <li key={i} className="text-xs" style={{ color: "#0B2545" }}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ChecklistCard = ({ title, items, testid }) => (
  <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
    <div className="font-display text-sm font-semibold mb-2" style={{ color: "#0B2545" }}>
      {title}
    </div>
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="text-xs flex items-start gap-2" style={{ color: "#475569" }}>
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: "#0F6D5D" }} />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

// =====================================================================
// 5. Sharps Safety
// =====================================================================
const SharpsSection = () => (
  <div className="space-y-3" data-testid="specimen-sharps">
    <SubIntro
      title="Sharps Safety & OSHA Compliance"
      subtitle="Needle handling, container usage, and Bloodborne Pathogen Standard requirements."
      icon={ShieldAlert}
    />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {SHARPS_PROCEDURES.map((p, i) => (
        <div key={i} className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}>
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
              style={{ background: "rgba(155,28,28,0.10)", color: "#9B1C1C" }}
            >
              <ShieldAlert className="h-4 w-4" />
            </span>
            <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
              {p.title}
            </div>
          </div>
          <p className="text-xs ml-10" style={{ color: "#475569" }}>{p.body}</p>
        </div>
      ))}
    </div>
    <ChecklistCard title="Pre-Collection Sharps Checklist" items={SHARPS_CHECKLIST} testid="sharps-checklist" />
  </div>
);

// =====================================================================
// 6. Exposure Procedures
// =====================================================================
const ExposureSection = () => (
  <div className="space-y-4" data-testid="specimen-exposure">
    <SubIntro
      title="Exposure Procedures"
      subtitle="Step-by-step response for a needlestick or blood/fluid exposure event."
      icon={AlertTriangle}
    />
    <ExposureSteps title="Needlestick Injury" tone="red" steps={EXPOSURE_NEEDLESTICK} testid="exposure-needlestick" />
    <ExposureSteps title="Blood / Body-Fluid Exposure" tone="navy" steps={EXPOSURE_BLOOD} testid="exposure-blood" />
  </div>
);

const ExposureSteps = ({ title, steps, tone, testid }) => {
  const palette =
    tone === "red"
      ? { color: "#9B1C1C", bg: "rgba(155,28,28,0.10)" }
      : { color: "#0B2545", bg: "rgba(11,37,69,0.08)" };
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span
          className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
          style={{ background: palette.bg, color: palette.color }}
        >
          <AlertTriangle className="h-4 w-4" />
        </span>
        <h3 className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>
          {title}
        </h3>
      </div>
      <ol className="space-y-2">
        {steps.map((s) => (
          <li key={s.n} className="flex items-start gap-3">
            <span
              className="grid place-items-center h-7 w-7 rounded-full text-xs font-bold shrink-0"
              style={{ background: palette.color, color: "#FFFFFF" }}
            >
              {s.n}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
                {s.title}
              </div>
              <p className="text-xs mt-0.5" style={{ color: "#475569" }}>{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

// =====================================================================
// 7. Supply Checklist (DYNAMIC — backend-hooked inventory)
// =====================================================================
const SuppliesSection = ({ onSessionExpired }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // id -> bool
  const [filter, setFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([listSupplies(), getSuppliesStats()]);
      setItems(list || []);
      setStats(s || null);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load supplies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const adjust = async (id, delta) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const updated = await adjustSupply(id, delta);
      setItems((prev) => prev.map((it) => (it.id === id ? updated : it)));
      // refresh stats lightly
      try { setStats(await getSuppliesStats()); } catch { /* ignore */ }
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Adjust failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const grouped = useMemo(() => {
    const visible = filter === "all" ? items : items.filter((i) => i.category === filter);
    const map = {};
    SUPPLY_CATEGORY_META.forEach((c) => { map[c.value] = []; });
    visible.forEach((i) => {
      if (!map[i.category]) map[i.category] = [];
      map[i.category].push(i);
    });
    return map;
  }, [items, filter]);

  return (
    <div className="space-y-4" data-testid="specimen-supplies">
      <SubIntro
        title="Collection Supply Checklist"
        subtitle="Live inventory. Adjust quantities; items at or below their alert threshold are flagged."
        icon={Package}
      />

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="supplies-stats">
          <StatTile label="Total items" value={stats.total} color="#0B2545" bg="rgba(11,37,69,0.06)" />
          <StatTile
            label="Below threshold"
            value={stats.low_count}
            color={stats.low_count > 0 ? "#9B1C1C" : "#0F6D5D"}
            bg={stats.low_count > 0 ? "rgba(155,28,28,0.10)" : "rgba(15,109,93,0.10)"}
          />
          <StatTile
            label="Out of stock"
            value={stats.out_of_stock_count}
            color={stats.out_of_stock_count > 0 ? "#9B1C1C" : "#475569"}
            bg={stats.out_of_stock_count > 0 ? "rgba(155,28,28,0.10)" : "#F1F5F9"}
          />
          <StatTile label="Categories" value={SUPPLY_CATEGORY_META.length} color="#7A5E12" bg="rgba(212,175,55,0.18)" />
        </div>
      )}

      {/* Category filters */}
      <div className="flex flex-wrap gap-1.5">
        <FilterChip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label={`All (${items.length})`}
          testid="supplies-filter-all"
        />
        {SUPPLY_CATEGORY_META.map((c) => {
          const cnt = items.filter((i) => i.category === c.value).length;
          return (
            <FilterChip
              key={c.value}
              active={filter === c.value}
              onClick={() => setFilter(c.value)}
              label={`${c.label} (${cnt})`}
              testid={`supplies-filter-${c.value}`}
            />
          );
        })}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} />
        </div>
      ) : (
        <div className="space-y-4">
          {SUPPLY_CATEGORY_META.map((cat) => {
            const list = grouped[cat.value] || [];
            if (filter !== "all" && filter !== cat.value) return null;
            if (list.length === 0) return null;
            return (
              <div key={cat.value} data-testid={`supplies-group-${cat.value}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Pill bg={cat.bg} color={cat.color}>{cat.label}</Pill>
                  <span className="text-xs" style={{ color: "#64748B" }}>{list.length} items</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {list.map((it) => (
                    <SupplyRow key={it.id} item={it} cat={cat} busy={!!busy[it.id]} onAdjust={adjust} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatTile = ({ label, value, color, bg }) => (
  <div className="rounded-xl border bg-white p-3" style={{ borderColor: "#E2E8F0" }}>
    <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "#64748B" }}>{label}</div>
    <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-md font-display text-lg font-bold"
         style={{ background: bg, color }}>
      {value}
    </div>
  </div>
);

const FilterChip = ({ active, label, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-xs px-3 py-1.5 rounded-full border transition-colors"
    style={{
      background: active ? "#0B2545" : "#FFFFFF",
      color: active ? "#FFD774" : "#475569",
      borderColor: active ? "#0B2545" : "#E2E8F0",
    }}
  >
    {label}
  </button>
);

const SupplyRow = ({ item, cat, busy, onAdjust }) => {
  const low = item.threshold > 0 && item.quantity <= item.threshold;
  const out = item.quantity === 0;
  return (
    <div
      className="rounded-xl border bg-white p-3 flex items-center gap-3"
      style={{
        borderColor: out ? "rgba(155,28,28,0.4)" : low ? "rgba(212,175,55,0.5)" : "#E2E8F0",
        borderLeftWidth: 4,
        borderLeftColor: cat.color,
      }}
      data-testid={`supply-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="font-display text-sm font-semibold truncate" style={{ color: "#0B2545" }}>
            {item.name}
          </div>
          {out ? (
            <Pill bg="rgba(155,28,28,0.10)" color="#9B1C1C">
              <AlertCircle className="h-2.5 w-2.5 mr-1" /> Out
            </Pill>
          ) : low ? (
            <Pill bg="rgba(212,175,55,0.18)" color="#7A5E12">
              <AlertCircle className="h-2.5 w-2.5 mr-1" /> Low
            </Pill>
          ) : null}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>
          Threshold {item.threshold} {item.unit}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={busy || item.quantity <= 0}
          onClick={() => onAdjust(item.id, -1)}
          data-testid={`supply-dec-${item.id}`}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <div
          className="min-w-[3rem] text-center font-display text-base font-bold tabular-nums px-2 py-1 rounded-md"
          style={{
            background: out ? "rgba(155,28,28,0.10)" : low ? "rgba(212,175,55,0.18)" : "#F1F5F9",
            color: out ? "#9B1C1C" : low ? "#7A5E12" : "#0B2545",
          }}
          data-testid={`supply-qty-${item.id}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : item.quantity}
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0"
          disabled={busy}
          onClick={() => onAdjust(item.id, +1)}
          data-testid={`supply-inc-${item.id}`}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 text-xs hidden md:inline-flex"
          disabled={busy}
          onClick={() => onAdjust(item.id, +10)}
          data-testid={`supply-inc10-${item.id}`}
        >
          +10
        </Button>
      </div>
    </div>
  );
};

// =====================================================================
// 8. Training & Competency
// =====================================================================
const TrainingSection = () => (
  <div className="space-y-4" data-testid="specimen-training">
    <SubIntro
      title="Training & Competency"
      subtitle="Onboarding modules, annual competency areas, and required credentialing."
      icon={GraduationCap}
    />

    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "#64748B" }}>
        Onboarding Modules
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {TRAINING_MODULES.map((m) => (
          <div key={m.id} className="rounded-xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}
               data-testid={`training-${m.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2">
                <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                      style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D" }}>
                  <GraduationCap className="h-4 w-4" />
                </span>
                <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
                  {m.title}
                </div>
              </div>
              <Pill bg="rgba(11,37,69,0.06)" color="#0B2545">{m.duration}</Pill>
            </div>
            <p className="text-xs ml-10 mb-2" style={{ color: "#475569" }}>{m.body}</p>
            <div className="ml-10">
              <div className="text-[10px] uppercase tracking-[0.16em] mb-1" style={{ color: "#64748B" }}>
                Competencies
              </div>
              <ul className="space-y-0.5">
                {m.competencies.map((c, i) => (
                  <li key={i} className="text-xs flex items-start gap-1.5" style={{ color: "#0B2545" }}>
                    <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" style={{ color: "#0F6D5D" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: "#64748B" }}>
        Annual Competency Areas
      </div>
      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-xs">
          <thead style={{ background: "rgba(11,37,69,0.04)" }}>
            <tr style={{ color: "#0B2545" }}>
              <th className="text-left font-semibold px-3 py-2 w-1/3">Area</th>
              <th className="text-left font-semibold px-3 py-2">Standard</th>
            </tr>
          </thead>
          <tbody>
            {ANNUAL_COMPETENCY.map((row, i) => (
              <tr key={row.area} style={{ background: i % 2 ? "#FFFFFF" : "rgba(11,37,69,0.02)" }}
                  data-testid={`competency-row-${i}`}>
                <td className="px-3 py-2 font-medium align-top" style={{ color: "#0B2545" }}>{row.area}</td>
                <td className="px-3 py-2 align-top" style={{ color: "#475569" }}>{row.standard}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] mb-2 flex items-center gap-1.5" style={{ color: "#64748B" }}>
        <FileBadge className="h-3.5 w-3.5" /> Required Staff Credentials
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {STAFF_CREDENTIAL_REQUIREMENTS.map((c) => (
          <div key={c.name} className="rounded-xl border bg-white p-3 flex items-start gap-3"
               style={{ borderColor: "#E2E8F0" }} data-testid={`credential-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                  style={{
                    background: c.required ? "rgba(155,28,28,0.10)" : "rgba(11,37,69,0.06)",
                    color: c.required ? "#9B1C1C" : "#0B2545",
                  }}>
              <FileBadge className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
                  {c.name}
                </div>
                {c.required ? (
                  <Pill bg="rgba(155,28,28,0.10)" color="#9B1C1C">Required</Pill>
                ) : (
                  <Pill bg="#F1F5F9" color="#475569">Conditional</Pill>
                )}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: "#64748B" }}>
                Renewal: {c.renewal}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// =====================================================================
// Main panel — sub-section nav + content router
// =====================================================================
export default function SpecimenTrainingPanel({ onSessionExpired }) {
  const [sub, setSub] = useState("tubes");

  return (
    <div className="space-y-4" data-testid="specimen-training-panel">
      <div className="flex flex-wrap gap-1.5">
        {SUBSECTIONS.map((s) => {
          const Icon = s.icon;
          const active = sub === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSub(s.id)}
              data-testid={`specimen-sub-${s.id}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{
                background: active ? "#0B2545" : "#FFFFFF",
                color: active ? "#FFD774" : "#475569",
                borderColor: active ? "#0B2545" : "#E2E8F0",
                fontWeight: active ? 600 : 500,
              }}
            >
              <Icon className="h-3.5 w-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {sub === "tubes" && <TubesSection />}
      {sub === "order" && <OrderOfDrawSection />}
      {sub === "guide" && <SpecimenGuideSection />}
      {sub === "handling" && <HandlingSection />}
      {sub === "sharps" && <SharpsSection />}
      {sub === "exposure" && <ExposureSection />}
      {sub === "supplies" && <SuppliesSection onSessionExpired={onSessionExpired} />}
      {sub === "training" && <TrainingSection />}
    </div>
  );
}
